import React, { useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector, useDispatch } from "react-redux";
import {
  selectToken,
  handleInvalidToken,
  handleAPIError,
} from "../../../utils/auth/authSlice";

// page components
import InitialConsentPage from "../InitialConsentPage";
import QuestionPage from "../../QuestionPage";
import ConfirmationPage from "./Pages/ConfirmationPage";
import SubmissionPage from "./Pages/SubmissionPage";
import AnswersPage from "./Pages/AnswersPage";
import ContactMethodPage from "./Pages/ContactMethodPage";
import ReferenceDocPage from "./Pages/ReferenceDocPage";
import LoadingPage from "../../LoadingPage";
import StepsPR from "../../PrimeReact/StepsPR";

// hooks
import { useScholarshipApp } from "../../../hooks/ScholarshipApp/useScholarshipApp";
import { useScholarshipAppOptions } from "../../../hooks/ScholarshipApp/useScholarshipAppOptions";
import { useSaveScholarshipAppField } from "../../../hooks/ScholarshipApp/useSaveScholarshipAppField";
import { useSaveScholarshipAppPage } from "../../../hooks/ScholarshipApp/useSaveScholarshipAppPage";
import { useCreateScholarshipApp } from "../../../hooks/ScholarshipApp/useCreateScholarshipApp";
import { useCalculateFinancialNeed } from "../../../hooks/ScholarshipApp/useCalculateFinancialNeed";
import { useSaveMarginalizedGroupTags } from "../../../hooks/ScholarshipApp/useSaveMarginalizedGroupTags";

export default function ScholarshipApp({ scholarshipId }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const scholarshipAppData = useScholarshipApp(
    scholarshipId,
    token,
    () => {
      dispatch(handleInvalidToken());
    },
    () => {
      dispatch(handleAPIError());
    }
  );
  const scholarshipAppOptions = useScholarshipAppOptions(
    scholarshipId,
    token,
    () => {
      dispatch(handleInvalidToken());
    },
    () => {
      dispatch(handleAPIError());
    }
  );

  const {
    mutate: createScholarshipApp,
    isLoading: isCreateScholarshipLoading,
  } = useCreateScholarshipApp(
    scholarshipId,
    token,
    handleInvalidToken,
    handleAPIError
  );

  const {
    mutate: saveField,
    status: saveFieldStatus,
  } = useSaveScholarshipAppField(
    scholarshipId,
    token,
    () => {
      dispatch(handleInvalidToken());
    },
    () => {
      dispatch(handleAPIError());
    }
  );
  const {
    mutate: savePage,
    isLoading: isSavePageLoading,
  } = useSaveScholarshipAppPage(
    scholarshipId,
    token,
    () => {
      dispatch(handleInvalidToken());
    },
    () => {
      dispatch(handleAPIError());
    }
  );

  const {
    mutate: calculateFinancialNeed,
    isLoading: isCalculateFinancialNeedLoading,
  } = useCalculateFinancialNeed(
    scholarshipId,
    token,
    handleInvalidToken,
    handleAPIError,
    scholarshipAppData.data
  );

  const {
    mutate: saveMarginalizedGroupTags,
    isLoading: isSaveMarginalizedGroupTagsLoading,
  } = useSaveMarginalizedGroupTags(
    scholarshipId,
    token,
    handleInvalidToken,
    handleAPIError,
    scholarshipAppData.data
  );

  const components = [
    ReferenceDocPage,
    ContactMethodPage,
    AnswersPage,
    SubmissionPage,
    ConfirmationPage,
  ];
  const dynamicQuestionsStartIdx = 0;

  if (
    !scholarshipAppData.isLoading &&
    typeof scholarshipAppData.data.questions !== "undefined"
  ) {
    Object.keys(scholarshipAppData.data.questions).forEach((val) => {
      components.splice(
        dynamicQuestionsStartIdx + parseInt(val, 10) - 1,
        0,
        QuestionPage
      );
    });
  }

  // Stepper is components.length-1 because ConfirmationPage is not a step
  const steps = Array(components.length - 1);
  steps.fill({});
  const steppers = (activeIndex) => {
    return (
      <StepsPR
        model={steps}
        activeIndex={activeIndex - 1}
        className="hide-stepper-label"
      />
    );
  };

  // 0 index array
  const Page = components[page - 1];

  const goToPage = (nextPage) => {
    queryClient.invalidateQueries(["scholarshipApp", 1]);
    Promise.resolve().then(() => {
      if (nextPage === "ConfirmationPage") {
        setPage(components.length - 1);
      } else {
        setIsTransitioning(true);
        setPage(nextPage);
        setIsTransitioning(false);
      }
    });
  };

  const spinnerStatus =
    isSavePageLoading ||
    isCreateScholarshipLoading ||
    isCalculateFinancialNeedLoading ||
    isSaveMarginalizedGroupTagsLoading ||
    scholarshipAppData.isLoading ||
    scholarshipAppData.isFetching ||
    scholarshipAppOptions.isLoading ||
    scholarshipAppOptions.isFetching;

  const pageInfo = {
    pageNum: page,
  };

  if (scholarshipAppData.isLoading || scholarshipAppOptions.isLoading) {
    return <LoadingPage />;
  }

  if (isTransitioning) {
    return <div />;
  }

  if (scholarshipAppData.data.is_consented === "0") {
    return (
      <InitialConsentPage
        savePage={createScholarshipApp}
        spinnerStatus={spinnerStatus}
        scholarshipAppData={scholarshipAppData}
      />
    );
  }

  if (scholarshipAppData.data.status === "Completed") {
    return <ConfirmationPage savePage={savePage} goToPage={goToPage} />;
  }

  return (
    <>
      <div>
        <Page
          goToPage={goToPage}
          scholarshipAppData={scholarshipAppData}
          scholarshipAppOptions={scholarshipAppOptions}
          saveField={saveField}
          saveFieldStatus={saveFieldStatus}
          spinnerStatus={spinnerStatus}
          pageInfo={pageInfo}
          savePage={savePage}
          steppers={steppers}
          calculateFinancialNeed={calculateFinancialNeed}
          saveMarginalizedGroupTags={saveMarginalizedGroupTags}
        />
      </div>
    </>
  );
}
